import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { Form, Field } from 'react-final-form';
import { isNil } from 'ramda';
import { IbanElement, useStripe, useElements } from '@stripe/react-stripe-js';

import type { FormRenderProps } from 'react-final-form';

import { InputGroup, Input } from '~/components/Inputs';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import cleanedFilename from '~/util/cleanedFilename';
import TopSectionContainer from '../components/TopSectionContainer';
import MidSectionContainer from '../components/MidSectionContainer';
import BottomSectionContainer from '../components/BottomSectionContainer';
import Dropdown from '~/components/Dropdown';
import StripeWrapper from '~/scenes/Settings/Subscription/components/StripeWrapper';
import Catalog from '~/Catalog';
import { FormUtils } from '~/components';
import Validation from '~/util/Validation';
import IBANElement from '../components/IBANElement';
import { asPriceString } from '~/util/money';

import TEST_ID from './index.testid';
import ErrorMessage from '../components/ErrorMessage';
import useErrorReporter from '~/hooks/useErrorReporter';
import { useStartAddBillingMethodMutation } from '~/graphql/types';
import type { SetupBillingInfo } from '../../types.flow';
import { PAYMENT_OPTIONS } from '~/scenes/Settings/Subscription/constants';

const text = {
  startAddBillingMethodErrorMessage: Catalog.genericUnknownErrorMessageShort,
  noCustomerSecretErrorMessage: Catalog.genericUnknownErrorMessageShort,
  noPaymentIdFromStripeErrorMessage:
    'Er kan geen verbinding worden gemaakt met de betalingsserver',
  infoStart: 'Doorlopende ',
  infoType: 'SEPA-machtiging.',
  info: ' Door het verstrekken van uw rekeningnummer (IBAN), geeft u toestemming aan DatHuis B.V., gevestigd aan de Rembrandtweg 41B, 1181 GE Amstelveen,  om overeenkomend met de opdracht doorlopend incasso-opdrachten te sturen naar uw bank om een bedrag van uw rekening af te schrijven. Als u het niet eens bent met deze afschrijving kunt u deze laten terugboeken. Neem hiervoor binnen acht weken na afschrijving contact op met uw bank. Vraag uw bank naar de voorwaarden.',
  totalPrice: 'Totaal gefactureerd ',
  nameLabel: 'Naam rekeninghouder',
  emailLabel: 'Facturatie e-mailadres',
  nextStepLabel: 'Bevestig',
  paymentOptionLabel: 'Betaalmethode',
};
type FormData = {
  name: string;
  email: string;
};
type Props = {
  onContinue: (paymentId: string) => void;
  onGoBack: (billingInfo: SetupBillingInfo) => void;
  totalPriceToPay: number;
  insertionLoading: boolean;
  insertionError: string | null;
  initialEmail: string | null;
  initialName: string | null;
};
const SetupPayment: React.FC<Props> = ({
  onContinue,
  onGoBack,
  totalPriceToPay,
  insertionLoading,
  insertionError,
  initialEmail,
  initialName,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const account = useCurrentAccount();
  const errorReporter = useErrorReporter();

  const [stripeErrorMsg, setStripeErrorMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [startAddBillingMethod, { error }] = useStartAddBillingMethodMutation();

  let errorMessage;

  if (stripeErrorMsg) {
    errorMessage = stripeErrorMsg;
  } else if (insertionError != null) {
    errorMessage = insertionError;
  } else if (error) {
    errorMessage = text.startAddBillingMethodErrorMessage;
  }

  const onFormSubmit = async args => {
    setLoading(true);
    setStripeErrorMsg(null);

    if (elements == null) {
      throw Error(
        `${cleanedFilename(__filename)} | Should not occur | elements == null`,
      );
    }
    if (stripe == null) {
      throw Error(
        `${cleanedFilename(__filename)} | Should not occur | stripe == null`,
      );
    }

    const ibanElement = elements.getElement(IbanElement);
    if (ibanElement == null) {
      throw Error(
        `${cleanedFilename(
          __filename,
        )} | Should not occur | ibanElement == null`,
      );
    }

    const result = await startAddBillingMethod({
      variables: { accountId: account.id },
    });

    const customerSecret = result?.data?.startAddBillingMethod?.customerSecret;

    if (isNil(customerSecret)) {
      errorReporter.captureException(
        new Error(`Missing customer secret`),
        'fatal',
      );
      setStripeErrorMsg(text.noCustomerSecretErrorMessage);

      setLoading(false);
      return;
    }

    return stripe
      .confirmSepaDebitSetup(customerSecret, {
        payment_method: {
          sepa_debit: ibanElement,
          billing_details: {
            name: args.name,
            email: args.email,
          },
        },
      })
      .then(result => {
        const paymentId = result?.setupIntent?.payment_method;

        if (paymentId == null) {
          const stripeError = result?.error?.message;

          errorReporter.captureException(
            new Error(
              stripeError == null
                ? text.noPaymentIdFromStripeErrorMessage
                : stripeError,
            ),
          );

          setStripeErrorMsg(
            stripeError == null
              ? text.noPaymentIdFromStripeErrorMessage
              : stripeError,
          );

          setLoading(false);
          return;
        }

        onContinue(paymentId);
        setLoading(false);
      });
  };

  return (
    <Container data-testid={TEST_ID.CONTAINER}>
      <Form
        validate={validate}
        onSubmit={onFormSubmit}
        initialValues={{
          name: initialName,
          email: initialEmail,
        }}
      >
        {({
          handleSubmit,
          submitting,
          pristine,
          // @ts-ignore
          values,
        }: FormRenderProps<SetupBillingInfo>) => {
          // TODO: Add a check for empty IBAN field here
          const submitDisabled =
            (!pristine && (isNil(values.email) || isNil(values.name))) ||
            submitting;

          return (
            <StyledForm id="payment-info" onSubmit={handleSubmit}>
              <TopSectionContainer headerText="Betaalmethode" withBorder />
              <MidSectionContainer>
                <InputFieldsContainer>
                  <InputGroup>
                    <Field name="name">
                      {({ input, meta: { error, touched, submitting } }) => (
                        <Input
                          disabled={submitting}
                          label={text.nameLabel}
                          error={FormUtils.showError(error, touched)}
                          {...input}
                        />
                      )}
                    </Field>
                  </InputGroup>
                  <InputGroup>
                    <Field name="email">
                      {({ input, meta: { error, touched, submitting } }) => (
                        <Input
                          disabled={submitting}
                          label={text.emailLabel}
                          error={FormUtils.showError(error, touched)}
                          {...input}
                        />
                      )}
                    </Field>
                  </InputGroup>
                  <Dropdown
                    options={PAYMENT_OPTIONS}
                    onChange={() => {}}
                    selectedOptionIdx={0}
                    label={text.paymentOptionLabel}
                  />
                  <InputGroup>
                    <IBANElement
                      elements={elements}
                      currentLast4={null}
                      onChange={() => setStripeErrorMsg(null)}
                    />
                  </InputGroup>

                  <InfoContainer>
                    {text.infoStart}
                    <InfoType>{text.infoType}</InfoType>
                    {text.info}
                  </InfoContainer>
                </InputFieldsContainer>

                <RowContainer>
                  <span>{text.totalPrice}</span>
                  <span data-testid={TEST_ID.TOTAL_PRICE}>
                    {asPriceString(totalPriceToPay)}
                  </span>
                </RowContainer>
                <ErrorMessage errorMessage={errorMessage} />
              </MidSectionContainer>

              <BottomSectionContainer
                onGoBack={() => {
                  onGoBack({
                    name: values?.name,
                    email: values?.email,
                  });
                }}
                nextStepButton={text.nextStepLabel}
                loading={insertionLoading || submitting || loading}
                disabled={
                  submitDisabled || insertionLoading || submitting || loading
                }
                submitButton={true}
              />
            </StyledForm>
          );
        }}
      </Form>
    </Container>
  );
};

const validate = ({ email, name }: FormData) => {
  const errors: { email: string | undefined; name: string | undefined } = {
    email: undefined,
    name: undefined,
  };

  if (Validation.String.isEmpty(email)) {
    errors.email = Catalog.noEmail;
  } else if (!Validation.Email.isValid(email)) {
    errors.email = Catalog.invalidEmail;
  }

  if (Validation.String.isEmpty(name)) {
    errors.name = Catalog.noName;
  }

  return errors;
};

const StyledForm = styled.form<{}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
`;

const RowContainer = styled.div<{}>`
  display: flex;
  height: 70px;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  font-weight: bold;

  ${({ theme }) => css`
    font-size: ${theme.fontSize('m')};
  `};
`;

const InputFieldsContainer = styled.div<{}>``;

const InfoContainer = styled.div<{}>`
  ${({ theme }) => css`
    font-size: ${theme.fontSize('s')};
    color: ${theme.color('grey')};
    line-height: ${theme.lineHeight('m')};
    margin-top: ${theme.space('xxxl')};
  `};
`;

const Container = styled.div<{}>`
  height: 100%;
`;

const InfoType = styled.span<{}>`
  text-decoration: underline;
`;

export default (props: Props) => (
  <StripeWrapper>
    <SetupPayment {...props} />
  </StripeWrapper>
);
